import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { Link } from "wouter";
import Navigation from "@/components/Navigation";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  DollarSign, 
  Users, 
  Target,
  TrendingUp,
  CheckCircle2,
  ArrowLeft,
  Mail,
  Globe,
  UserPlus
} from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const formSchema = z.object({
  fullName: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Please enter a valid email address"),
  websiteUrl: z.string().url("Please enter a valid website URL").optional().or(z.literal("")),
  companyName: z.string().optional(),
  monthlyTraffic: z.string().min(1, "Please select your monthly traffic"),
  promotionMethods: z.array(z.string()).min(1, "Please select at least one promotion method"),
  experience: z.string().min(1, "Please select your affiliate marketing experience"),
  whyAffiliate: z.string().min(50, "Please provide at least 50 characters explaining why you want to join"),
  agreeToTerms: z.boolean().refine(val => val === true, "You must agree to the terms and conditions"),
});

type FormData = z.infer<typeof formSchema>;

const trafficOptions = [
  { value: "0-1k", label: "0 - 1,000 visitors/month" },
  { value: "1k-10k", label: "1,000 - 10,000 visitors/month" },
  { value: "10k-50k", label: "10,000 - 50,000 visitors/month" },
  { value: "50k-100k", label: "50,000 - 100,000 visitors/month" },
  { value: "100k+", label: "100,000+ visitors/month" },
];

const promotionMethods = [
  { id: "blog", label: "Blog Posts & Reviews" },
  { id: "social", label: "Social Media" },
  { id: "email", label: "Email Marketing" },
  { id: "ads", label: "Paid Advertising" },
  { id: "youtube", label: "YouTube Videos" },
  { id: "influencer", label: "Influencer Partnerships" },
];

const experienceOptions = [
  { value: "beginner", label: "Beginner (0-1 years)" },
  { value: "intermediate", label: "Intermediate (1-3 years)" },
  { value: "advanced", label: "Advanced (3+ years)" },
];

export default function AffiliateSignupPage() {
  const { toast } = useToast();
  const [isSubmitted, setIsSubmitted] = useState(false);

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      fullName: "",
      email: "",
      websiteUrl: "",
      companyName: "",
      monthlyTraffic: "",
      promotionMethods: [],
      experience: "",
      whyAffiliate: "",
      agreeToTerms: false,
    },
  });

  const submitMutation = useMutation({
    mutationFn: async (data: FormData) => {
      return apiRequest('POST', '/api/affiliate/signup', data);
    },
    onSuccess: () => {
      setIsSubmitted(true);
      toast({
        title: "Application Submitted!",
        description: "We'll review your application and get back to you within 2-3 business days.",
      });
    },
    onError: (error) => {
      toast({
        title: "Submission Failed",
        description: "There was an error submitting your application. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = (data: FormData) => {
    submitMutation.mutate(data);
  };

  if (isSubmitted) {
    return (
      <>
        <Navigation />
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
          <div className="container mx-auto px-4 py-16 max-w-2xl">
            <Card className="text-center">
              <CardContent className="pt-8 pb-8">
                <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
                <h1 className="text-3xl font-bold mb-4">Application Submitted!</h1>
                <p className="text-muted-foreground mb-6">
                  Thank you for your interest in joining our affiliate program. We'll review your application and get back to you within 2-3 business days.
                </p>
                <div className="flex gap-4 justify-center">
                  <Link href="/programs">
                    <Button variant="outline" data-testid="button-back-programs">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Back to Programs
                    </Button>
                  </Link>
                  <Link href="/affiliate/info">
                    <Button data-testid="button-learn-more">
                      Learn More About Program
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-amber-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="container mx-auto px-4 py-8 max-w-4xl">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-2 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 px-4 py-2 rounded-full text-sm font-medium mb-4">
              <DollarSign className="w-4 h-4" />
              Affiliate Program
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              Join Our Affiliate Program
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Start earning commissions by promoting SnuggleCrafters to your audience. 
              Fill out the form below to apply for our affiliate program.
            </p>
          </div>

          {/* Benefits Preview */}
          <div className="grid md:grid-cols-3 gap-4 mb-8">
            <Card className="text-center p-4">
              <DollarSign className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <h3 className="font-semibold mb-1">Up to 30% Commission</h3>
              <p className="text-sm text-muted-foreground">Competitive rates with performance bonuses</p>
            </Card>
            <Card className="text-center p-4">
              <Users className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <h3 className="font-semibold mb-1">Marketing Support</h3>
              <p className="text-sm text-muted-foreground">Ready-to-use banners and content</p>
            </Card>
            <Card className="text-center p-4">
              <Target className="w-8 h-8 text-orange-500 mx-auto mb-2" />
              <h3 className="font-semibold mb-1">Easy Tracking</h3>
              <p className="text-sm text-muted-foreground">Comprehensive dashboard and analytics</p>
            </Card>
          </div>

          {/* Application Form */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <UserPlus className="w-5 h-5" />
                Affiliate Application
              </CardTitle>
              <CardDescription>
                Tell us about yourself and how you plan to promote SnuggleCrafters
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                  {/* Personal Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Personal Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Full Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Your full name" {...field} data-testid="input-full-name" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Email Address *</FormLabel>
                            <FormControl>
                              <Input placeholder="your@email.com" {...field} data-testid="input-email" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="websiteUrl"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Website URL</FormLabel>
                            <FormControl>
                              <Input placeholder="https://yourwebsite.com" {...field} data-testid="input-website" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="companyName"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Company Name (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Your company name" {...field} data-testid="input-company" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  </div>

                  {/* Marketing Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Marketing Information</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="monthlyTraffic"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Monthly Website Traffic *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-traffic">
                                  <SelectValue placeholder="Select traffic range" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {trafficOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="experience"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Affiliate Marketing Experience *</FormLabel>
                            <Select onValueChange={field.onChange} value={field.value}>
                              <FormControl>
                                <SelectTrigger data-testid="select-experience">
                                  <SelectValue placeholder="Select experience level" />
                                </SelectTrigger>
                              </FormControl>
                              <SelectContent>
                                {experienceOptions.map((option) => (
                                  <SelectItem key={option.value} value={option.value}>
                                    {option.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="promotionMethods"
                      render={() => (
                        <FormItem>
                          <FormLabel>How do you plan to promote SnuggleCrafters? *</FormLabel>
                          <FormDescription>Select all that apply</FormDescription>
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                            {promotionMethods.map((method) => (
                              <FormField
                                key={method.id}
                                control={form.control}
                                name="promotionMethods"
                                render={({ field }) => (
                                  <FormItem className="flex items-center space-x-2">
                                    <FormControl>
                                      <Checkbox
                                        checked={field.value?.includes(method.id)}
                                        onCheckedChange={(checked) => {
                                          return checked
                                            ? field.onChange([...field.value, method.id])
                                            : field.onChange(
                                                field.value?.filter((value) => value !== method.id)
                                              );
                                        }}
                                        data-testid={`checkbox-${method.id}`}
                                      />
                                    </FormControl>
                                    <FormLabel className="text-sm font-normal">
                                      {method.label}
                                    </FormLabel>
                                  </FormItem>
                                )}
                              />
                            ))}
                          </div>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Additional Information */}
                  <div className="space-y-4">
                    <h3 className="text-lg font-semibold">Tell Us More</h3>
                    <FormField
                      control={form.control}
                      name="whyAffiliate"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Why do you want to join our affiliate program? *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Tell us about your interest in promoting SnuggleCrafters and how it aligns with your audience..."
                              className="min-h-[100px]"
                              {...field}
                              data-testid="textarea-why-affiliate"
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Terms and Conditions */}
                  <FormField
                    control={form.control}
                    name="agreeToTerms"
                    render={({ field }) => (
                      <FormItem className="flex items-center space-x-2">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                            data-testid="checkbox-terms"
                          />
                        </FormControl>
                        <FormLabel className="text-sm">
                          I agree to the{" "}
                          <Link href="/terms" className="text-primary underline">
                            Terms and Conditions
                          </Link>{" "}
                          and{" "}
                          <Link href="/privacy" className="text-primary underline">
                            Privacy Policy
                          </Link>
                        </FormLabel>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Submit Button */}
                  <div className="flex gap-4">
                    <Link href="/programs">
                      <Button variant="outline" type="button" data-testid="button-back">
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back
                      </Button>
                    </Link>
                    <Button 
                      type="submit" 
                      disabled={submitMutation.isPending}
                      className="bg-gradient-to-r from-orange-600 to-amber-600 hover:from-orange-700 hover:to-amber-700"
                      data-testid="button-submit-application"
                    >
                      {submitMutation.isPending ? "Submitting..." : "Submit Application"}
                      <TrendingUp className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </form>
              </Form>
            </CardContent>
          </Card>
        </div>
      </div>
    </>
  );
}